
<div class="h-full flex flex-col space-y-6 relative">
    
    <!-- ✅ NEW: Visual Alert Overlay (Flashes green when new patient arrives) -->
    @if(hasNewPatientAlert()) {
        <div class="fixed inset-0 pointer-events-none z-[100] animate-pulse-green border-[12px] border-emerald-500/40 rounded-3xl"></div>
        <div class="fixed top-20 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-6 py-3 rounded-full shadow-2xl z-[101] font-bold animate-bounce-in flex items-center gap-3">
            <i class="fa-solid fa-user-plus"></i>
            <span>มีคนไข้ใหม่เข้าคิว! (New Patient in Queue)</span>
        </div>
    }

    <div class="flex justify-between items-center">
        <div>
            <h1 class="text-2xl font-bold text-gray-800"><i class="fa-solid fa-user-doctor mr-2 text-blue-600"></i>{{ 'backOffice.telemedicine.commandCenter' | translate }}</h1>
            <p class="text-gray-500">{{ 'backOffice.telemedicine.subtitle' | translate }}</p>
        </div>
        <div class="flex items-center gap-2">
            <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                <i class="fa-solid fa-circle text-[8px] mr-2 animate-pulse text-green-500"></i> {{ 'backOffice.telemedicine.systemOnline' | translate }}
            </span>
        </div>
    </div>

    <!-- Policy Reminder Banner -->
    <div class="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-md flex items-start gap-3 shadow-sm">
        <i class="fa-solid fa-camera text-blue-500 mt-1"></i>
        <div>
            <p class="text-sm font-bold text-blue-800">ระเบียบการบันทึกภาพ (Safety Protocol)</p>
            <p class="text-xs text-blue-600">
                เพื่อความปลอดภัยและการตรวจสอบย้อนหลัง <strong>กรุณาบันทึกภาพหน้าจอ (Screenshot)</strong> ขณะทำการตรวจรักษาทุกครั้ง 
            </p>
        </div>
    </div>
    
    <!-- SECTION 1: MY ACTIVE CASES -->
    @if(myActiveCases().length > 0) {
        <div class="bg-orange-50 border border-orange-200 rounded-xl shadow-sm overflow-hidden animate-fade-in">
             <div class="p-3 bg-orange-100/50 border-b border-orange-200 flex justify-between items-center">
                <h3 class="font-bold text-orange-800 flex items-center gap-2">
                    <i class="fa-solid fa-notes-medical"></i> งานที่กำลังทำ (In Progress by Me)
                </h3>
                <span class="bg-orange-200 text-orange-800 text-xs px-2 py-1 rounded-full font-bold">{{ myActiveCases().length }} เคส</span>
            </div>
            <div class="divide-y divide-orange-100">
                @for(p of myActiveCases(); track p.id) {
                     <div class="p-4 bg-white hover:bg-orange-50/30 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div class="flex items-center gap-4">
                            <div class="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 bg-orange-100 text-orange-600">
                                {{ p.customerName?.[0] }}
                            </div>
                            <div>
                                <div class="flex items-center gap-2">
                                    <p class="font-bold text-lg text-gray-800">{{ p.customerName }}</p>
                                    <span class="text-xs font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full border border-orange-200">
                                        กำลังตรวจ (Engaged)
                                    </span>
                                </div>
                                <div class="flex gap-2 text-sm text-gray-500">
                                    <span><i class="fa-solid fa-clock mr-1"></i> เริ่มเมื่อ: {{ p.engaged_at | date:'HH:mm' }}</span>
                                </div>
                            </div>
                        </div>
                        
                        <div class="flex items-center gap-3 self-end md:self-auto">
                             <button (click)="releaseCase(p)" [disabled]="isReleasing()" class="px-3 py-2 text-xs font-bold text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors">
                                <i class="fa-solid fa-lock-open mr-1"></i> คืนคิว (Release)
                            </button>
                            <button (click)="startCall(p)" class="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-bold shadow-sm transition-all flex items-center gap-2">
                                <i class="fa-solid fa-video"></i> VDO Call
                            </button>
                            <button (click)="openReview(p)" class="px-4 py-2 rounded-lg font-bold shadow-sm transition-all text-white bg-blue-600 hover:bg-blue-700">
                                <i class="fa-solid fa-file-signature mr-1"></i> ตรวจต่อ (Resume)
                            </button>
                        </div>
                     </div>
                }
            </div>
        </div>
    }

    <!-- SECTION 2: QUEUE LIST -->
    <div id="patient-queue-list" class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex-grow flex flex-col">
        <div class="p-4 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h3 class="font-bold text-slate-700 flex items-center gap-2">
                <i class="fa-solid fa-users-line"></i> {{ 'backOffice.telemedicine.waitingRoom' | translate }}
            </h3>
            
            <div class="flex items-center gap-2 bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
                <button (click)="setFilter('all')" [class]="activeFilter() === 'all' ? 'bg-slate-800 text-white shadow' : 'text-slate-500 hover:bg-slate-100'" class="px-3 py-1.5 rounded-md text-xs font-bold transition-all">ทั้งหมด ({{ prescriptions().length }})</button>
                <button (click)="setFilter('walkin')" [class]="activeFilter() === 'walkin' ? 'bg-blue-600 text-white shadow' : 'text-slate-500 hover:bg-slate-100'" class="px-3 py-1.5 rounded-md text-xs font-bold transition-all">Walk-in</button>
                <button (click)="setFilter('referral')" [class]="activeFilter() === 'referral' ? 'bg-purple-600 text-white shadow' : 'text-slate-500 hover:bg-slate-100'" class="px-3 py-1.5 rounded-md text-xs font-bold transition-all">Referral</button>
            </div>

            <button (click)="loadPrescriptions()" class="text-sm text-blue-600 hover:underline ml-auto sm:ml-0"><i class="fa-solid fa-refresh mr-1"></i> {{ 'backOffice.telemedicine.refresh' | translate }}</button>
        </div>

        <div class="divide-y divide-slate-100 overflow-y-auto flex-grow custom-scrollbar">
            @for(p of generalQueue(); track p.id) {
                @let isEngagedByOther = p.status === 'ENGAGED';
                
                <div class="p-4 transition-all duration-200 flex flex-col md:flex-row md:items-center justify-between gap-4 relative overflow-hidden"
                     [class]="isEngagedByOther ? 'bg-slate-100 opacity-80' : 'hover:bg-blue-50/30'">
                    
                    <div class="absolute top-0 left-0 w-1 h-full" [class]="isEngagedByOther ? 'bg-slate-400' : (p.referred_by_store_name ? 'bg-purple-500' : 'bg-blue-500')"></div>

                    <div class="flex items-center gap-4 pl-2">
                        <div class="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 border-2"
                             [class]="isEngagedByOther ? 'bg-slate-200 text-slate-500 border-slate-300' : (p.referred_by_store_name ? 'bg-purple-100 text-purple-600 border-purple-200' : 'bg-blue-100 text-blue-600 border-blue-200')">
                            {{ p.customerName?.[0] }}
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <p class="font-bold text-lg text-gray-800">{{ p.customerName }}</p>
                                @if(p.referred_by_store_name) {
                                    <span class="px-2 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full border border-purple-200">
                                        ส่งจาก: {{ p.referred_by_store_name }}
                                    </span>
                                }
                            </div>
                            <p class="text-xs text-gray-500">รอตั้งแต่: {{ p.createdAt | date:'HH:mm' }}</p>
                            
                            @if(isEngagedByOther) {
                                <div class="mt-1 text-[10px] font-bold text-slate-500 italic">
                                    <i class="fa-solid fa-lock mr-1"></i> ตรวจโดย: {{ p.engaged_by_doctor_name || 'หมอท่านอื่น' }}
                                </div>
                            } @else {
                                <div class="flex gap-1 mt-1 flex-wrap">
                                    @for(sym of p.symptoms; track sym) {
                                        <span class="px-2 py-0.5 bg-red-50 text-red-600 text-[10px] rounded-full border border-red-100 font-medium">{{ sym }}</span>
                                    }
                                </div>
                            }
                        </div>
                    </div>

                    <div class="flex items-center gap-3 self-end md:self-auto">
                        @if(isEngagedByOther) {
                            <span class="text-slate-400 text-xs italic bg-slate-200/50 px-3 py-2 rounded-lg border border-slate-300">
                                <i class="fa-solid fa-ban mr-1"></i> ไม่ว่าง (Busy)
                            </span>
                        } @else {
                            <button (click)="startCall(p)" [disabled]="isCalling()" class="bg-white border border-slate-300 text-slate-700 hover:text-emerald-600 hover:border-emerald-300 px-4 py-2 rounded-lg font-bold shadow-sm transition-all flex items-center gap-2">
                                <i class="fa-solid fa-video"></i>
                                <span class="hidden sm:inline">VDO Call</span>
                            </button>
                            
                            @if(isDoctor()) {
                                <button (click)="openReview(p)"
                                        class="px-4 py-2 rounded-lg font-bold shadow-sm transition-all text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-2">
                                    <span>ตรวจและสั่งยา</span> <i class="fa-solid fa-chevron-right text-xs"></i>
                                </button>
                            }
                        }
                    </div>
                </div>
            } @empty {
                <div class="p-20 text-center text-slate-400 flex flex-col items-center justify-center h-full">
                    <div class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4"><i class="fa-solid fa-mug-hot text-3xl opacity-30"></i></div>
                    <p class="text-lg font-bold text-slate-500">{{ 'backOffice.telemedicine.empty.title' | translate }}</p>
                    <p class="text-xs mt-2">{{ 'backOffice.telemedicine.empty.subtitle' | translate }}</p>
                </div>
            }
        </div>
    </div>
</div>

<app-smart-prescription-modal
  [isOpen]="isModalOpen()"
  [prescription]="selectedPrescription()"
  (closeModal)="handleModalClose()"
  (prescriptionReviewed)="handleReviewComplete()">
</app-smart-prescription-modal>

<!-- FIXED: สำหรับฝั่งหมอ isIncoming ต้องเป็น FALSE เพื่อให้แสดงหน้าจอเตรียมห้องตรวจ -->
<app-video-call-modal
  [isOpen]="isCallModalOpen()"
  [isIncoming]="false"
  [roomName]="activeRoomName()"
  [displayName]="currentUser()?.name || 'Doctor'"
  (callEnded)="isCallModalOpen.set(false)"
  (imageCaptured)="null">
</app-video-call-modal>

<!-- Confirmation Modal -->
<app-confirmation-modal
  [isOpen]="confirmModalState().isOpen"
  [title]="confirmModalState().title"
  [message]="confirmModalState().message"
  [confirmButtonText]="confirmModalState().confirmButtonText"
  [confirmButtonClass]="confirmModalState().confirmButtonClass"
  (confirm)="onConfirmAction()"
  (cancel)="closeConfirmModal()">
</app-confirmation-modal>

<style>
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

@keyframes pulse-green {
  0% { border-color: rgba(16, 185, 129, 0.4); }
  50% { border-color: rgba(16, 185, 129, 0.8); }
  100% { border-color: rgba(16, 185, 129, 0.4); }
}
.animate-pulse-green { animation: pulse-green 1s infinite; }

@keyframes bounce-in {
  0% { transform: translate(-50%, -100%) scale(0.8); opacity: 0; }
  60% { transform: translate(-50%, 20%) scale(1.1); opacity: 1; }
  100% { transform: translate(-50%, 0) scale(1); }
}
.animate-bounce-in { animation: bounce-in 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
</style>
